import React, { useCallback, useMemo } from 'react';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';

import { EExpand } from '@r1-frontend/api-domru/api-content/_dto/tariff-lines-common-types';
import getTariffLineByAlias from '@r1-frontend/api-domru/api-content/v1/tariff-line-by-alias';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import ErrorPage from '~/pages/_error';
import { ITariffLine } from '~/src/entities/tariff-lines';
import Layout from '~/src/features/_layout';
import { initialProps } from '~/src/helpers/basePageFunc';
import getRequester from '~/src/helpers/getRequester';
import { nonPermanentRedirect } from '~/src/helpers/url';
import { selectCompanyName } from '~/src/store/selectors/city';

const TariffLines = dynamic(() => import('~/src/features/tariffLines'));

interface Props {
    city: ICurrentCity,
    tariffLine?: ITariffLine | false,
    companyName: string,
}

const TariffLinesPage: NextPage<Props> = ({ city, tariffLine, companyName }) => {
    const cityInfo = useMemo(() => [
        { name: '{city_sale_phone}', value: city.phone },
        { name: '{city_name}', value: city.name },
        { name: '{city_dat_name}', value: city.cityTo },
        { name: '{city_pred_name}', value: city.cityIn },
        { name: '{city_support_phone}', value: city.supportPhone },
        { name: '{city_sale_phone}', value: city.phone },
    ], [city]);

    const renderCityInfo = useCallback((variables: { name: string, value: string }[], inputString?: string) => {
        let output = inputString;
        variables.forEach(variable => {
            output = output?.replaceAll(variable.name, variable.value);
        });

        return output;
    }, []);

    const title = useMemo(() => {
        if (!tariffLine) {
            return `Провайдер интернета ${companyName} в ${city.cityIn}`;
        }
        return renderCityInfo(cityInfo, tariffLine.seo_title);
    }, [tariffLine, city, renderCityInfo, cityInfo]);

    const description = useMemo(() => {
        if (!tariffLine) {
            return `Официальный сайт провайдера ${companyName} в ${city.cityIn}. Подключить быстрый Интернет, Цифровое телевидение и онлайн-кинотеатр, Домофон и Видеонаблюдение. Адреса и телефоны офисов.`;
        }
        return renderCityInfo(cityInfo, tariffLine.seo_description);
    }, [tariffLine, city, renderCityInfo, cityInfo]);

    if (!tariffLine) {
        return <ErrorPage statusCode={404}/>;
    }

    return (
        <Layout
            title={title}
            description={description}
            withMenu={true}
            withChat={true}
            withFooter={true}
            bgColor={COLORS.BgMain}
        >
            <TariffLines tariffLine={tariffLine}/>
        </Layout>
    );
};

TariffLinesPage.getInitialProps = async(options) => {
    const { validatedAccessToken } = await initialProps(options, { isNeedAuthData: true });

    const { store, query: { alias }, res, req } = options;

    if (validatedAccessToken) {
        nonPermanentRedirect({ res, location: '/lk/change-tariff' });
    }

    const { getState } = store;

    const state = getState();
    const {
        city: { currentCity: city },
    } = state;

    const companyName = selectCompanyName(state);
    const aliasQuery = typeof alias === 'string' ? alias : '';
    const doRequest = getRequester(req);

    const tariffLineResp = await doRequest.apiContent<ITariffLine>(getTariffLineByAlias(
        { expand: [EExpand.Tariffs, EExpand.RecommendedTariffLines, EExpand.Contents] },
        aliasQuery,
    ));
    const tariffLine = tariffLineResp.isSuccess ? tariffLineResp.payload : false;

    const { redirect_url, is_active } = tariffLine || {};

    if (tariffLine && !is_active && redirect_url) {
        nonPermanentRedirect({ res, location: redirect_url });
    }

    if (!tariffLine && res) {
        res.statusCode = 404;
    }

    return { city, tariffLine, companyName };
};

export default TariffLinesPage;
